import { useCallback } from 'react'
import { ColumnTable } from 'arquero'
import useGlobalDownloadTrigger from 'utils/useGlobalDownloadTrigger'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import translatePinnedMorphotypeSelection from 'components/cell-visualizations/translatePinnedMorphotypeSelection'
import useEventsManager from 'redux/slices/hooks/useEventsManager'
import { getColumn, getColumnNames } from './arrow/arqueroHelpers'
import { CellInfo } from './tsv/types'

interface UseSessionDownloadReturn {
  /**
   * Download the current session as JSON
   * @param fileName - Name of the file to download
   * @param includeCellsData - Optional flag to include cellsData with DL embeddings (default: false)
   *                          Note: Including cellsData significantly increases file size and memory usage
   */
  downloadSession: (fileName: string, includeCellsData?: boolean) => Promise<void>
}

/**
 * Handle BigInt serialization for JSON and exclude non-serializable objects
 */
function handleBigInt(_key: string, value: unknown) {
  if (typeof value === 'bigint') {
    return value.toString()
  }
  // Exclude non-serializable objects (like Arquero ColumnTable, functions, etc.)
  if (value && typeof value === 'object') {
    // Check if it's a ColumnTable or has methods that would cause serialization issues
    if ('column' in value && typeof (value as { column?: unknown }).column === 'function') {
      return undefined // Exclude Arquero ColumnTable
    }
    // Exclude objects with .at method that aren't arrays (could be Arrow vectors)
    // This must come before any iteration to prevent "cols[i].at is not a function" errors
    if (
      'at' in value &&
      typeof (value as { at?: unknown }).at === 'function' &&
      !Array.isArray(value)
    ) {
      return undefined
    }
    // Also check for Arrow vector-like objects (they have get, toArray, length properties)
    if (
      'get' in value &&
      'toArray' in value &&
      'length' in value &&
      typeof (value as { toArray?: unknown }).toArray === 'function' &&
      !Array.isArray(value)
    ) {
      return undefined // Exclude Arrow vectors
    }
  }
  return value
}

/**
 * Extracts DL embeddings from DataFrame and converts to cellsData format for export.
 * DL embeddings are stored as columns "0" through "63" (64 dimensions).
 * 
 * @param dataFrame - Arquero DataFrame containing cell data
 * @returns Array of CellInfo objects with DL embeddings included, or undefined if no DataFrame
 */
function extractCellsDataWithEmbeddings(dataFrame: ColumnTable | undefined): CellInfo[] | undefined {
  if (!dataFrame) {
    return undefined
  }

  const columnNames = getColumnNames(dataFrame)
  const cellIdColumn = getColumn<string>(dataFrame, 'CELL_ID')
  
  if (cellIdColumn.length === 0) {
    return undefined
  }

  // Extract DL embedding columns (0-63)
  const dlEmbeddingColumns: number[][] = []
  for (let i = 0; i < 64; i += 1) {
    const columnName = String(i)
    if (columnNames.includes(columnName)) {
      dlEmbeddingColumns[i] = getColumn<number>(dataFrame, columnName)
    } else {
      // If column doesn't exist, fill with zeros
      dlEmbeddingColumns[i] = new Array(cellIdColumn.length).fill(0)
    }
  }

  // Extract all columns once before the loop to avoid redundant calls
  const columnsMap: Map<string, (string | number | boolean | null)[]> = new Map()
  columnNames.forEach((colName) => {
    // Skip DL embedding columns (0-63) as we'll add them separately
    const colNum = Number(colName)
    if (!Number.isNaN(colNum) && colNum >= 0 && colNum <= 63 && colName === String(colNum)) {
      return
    }
    columnsMap.set(colName, getColumn<string | number | boolean | null>(dataFrame, colName))
  })

  // Build cellsData array with all columns including DL embeddings
  const cellsData: CellInfo[] = []
  const numCells = cellIdColumn.length

  for (let i = 0; i < numCells; i += 1) {
    const cellInfo: Partial<CellInfo> = {
      CELL_ID: cellIdColumn[i],
    }

    // Access pre-extracted columns
    columnsMap.forEach((column, colName) => {
      if (column.length > i) {
        cellInfo[colName as keyof CellInfo] = column[i] as CellInfo[keyof CellInfo]
      }
    })

    // Store DL embeddings (0-63) directly into cellInfo as individual columns
    for (let j = 0; j < 64; j += 1) {
      const colKey = String(j) as keyof CellInfo
      const value = dlEmbeddingColumns[j] && dlEmbeddingColumns[j].length > i ? dlEmbeddingColumns[j][i] : 0
      cellInfo[colKey] = value as unknown as CellInfo[keyof CellInfo]
    }

    cellsData.push(cellInfo as CellInfo)
  }

  return cellsData
}

/**
 * Hook for downloading cell visualization session as JSON
 *
 * Features:
 * - Integration with Enhanced Download UX
 * - Filename editing
 * - Auto-download support
 * - BigInt serialization support
 * - Version tracking
 *
 * Version history:
 * - 1.0.0: Initial release
 * - 1.0.1: Encode BigInt values from Arrow as strings (for any integer columns)
 * - 1.0.2: Optional inclusion of DL embeddings in cellsData when exporting (opt-in feature)
 *
 * @example
 * ```tsx
 * const { downloadSession } = useSessionDownload()
 *
 * // Download session
 * await downloadSession('my-session.json')
 * ```
 */
const useSessionDownload = (): UseSessionDownloadReturn => {
  const { triggerSyncDownload } = useGlobalDownloadTrigger()
  const { cellVisualizations } = useCellVisualizationsSlice()
  const eventsManager = useEventsManager()

  /**
   * Download session as JSON
   */
  const downloadSession = useCallback(
    async (fileName: string, includeCellsData = false) => {
      // Extract fields that should be excluded from export (same as saveSession)
      const {
        // Frontend-only fields that should NOT be exported
        // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
        zoomThresholds,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
        dataFrame,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
        fileName: cellVisualizationsFileName,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
        dataRevision,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
        isDirty,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
        sessionData,
        ...essentialCellVisualizations
      } = cellVisualizations

      // Extract cellsData with DL embeddings from DataFrame for export (only if requested)
      let cellsData: CellInfo[] | undefined
      if (includeCellsData) {
        if (!dataFrame) {
          console.warn('Export with cellsData requested but no dataFrame available')
        } else {
          cellsData = extractCellsDataWithEmbeddings(dataFrame)
          if (!cellsData) {
            console.warn('Export with cellsData requested but extraction returned undefined')
          }
        }
      }

      // Prepare session data with version
      const cellAnalysisWithVersion = {
        version: '1.0.2',
        ...{
          ...essentialCellVisualizations,
          pinnedCells: cellVisualizations.pinnedCells?.map((pc) =>
            translatePinnedMorphotypeSelection(pc)
          ),
          // Reset zoomThresholds because it gets way too big and crashes the download
          zoomThresholds: undefined,
          // Include cellsData with DL embeddings only if explicitly requested and successfully extracted
          ...(includeCellsData && cellsData ? { cellsData } : {}),
        },
      }

      // Send analytics event
      eventsManager.sendExportEvent(cellVisualizations.fileName)

      await triggerSyncDownload({
        type: 'json',
        fileName,
        generateFile: () => {
          const jsonContent = JSON.stringify(cellAnalysisWithVersion, handleBigInt, 1)
          return new Blob([jsonContent], { type: 'application/json' })
        },
      })
    },
    [cellVisualizations, eventsManager, triggerSyncDownload]
  )

  return {
    downloadSession,
  }
}

export default useSessionDownload
